/**
 * Human-readable task ids (`AP-1234`, `TASK-42`) are derived as
 * `<namespace.key>-<taskNumber>`. They are not stored; callers parse or
 * rebuild them. UUID task ids stay a separate lookup path.
 */

/** Namespace key: uppercase alphanumeric, 1–10 chars (matches NamespaceKeySchema). */
const DISPLAY_ID_PATTERN = /^([A-Za-z0-9]{1,10})-(\d+)$/;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** PostgreSQL INTEGER max — `core_tasks.task_number` is int4, not bigint. */
const PG_INT4_MAX = 2_147_483_647;

export function isTaskUuid(value: string): boolean {
  return UUID_PATTERN.test(value.trim());
}

export function parseTaskDisplayId(
  value: string,
): { key: string; taskNumber: number } | null {
  const match = DISPLAY_ID_PATTERN.exec(value.trim());
  const key = match?.[1];
  const numberPart = match?.[2];
  if (!key || !numberPart) return null;
  const taskNumber = Number(numberPart);
  if (
    !Number.isSafeInteger(taskNumber) ||
    taskNumber < 1 ||
    taskNumber > PG_INT4_MAX
  ) {
    return null;
  }
  return { key: key.toUpperCase(), taskNumber };
}

/**
 * Trim and canonicalize a pasted task lookup: UUID unchanged, display id
 * uppercased with leading zeros on the number dropped (`ap-0012` → `AP-12`).
 * Returns null when the string is neither shape.
 */
export function normalizeTaskLookupId(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (isTaskUuid(trimmed)) return trimmed;
  const parsed = parseTaskDisplayId(trimmed);
  if (!parsed) return null;
  return `${parsed.key}-${parsed.taskNumber}`;
}
